import * as dtt from "../../../data/model/data-types.js";
import { Component } from "../../Component.js";

function resolveValue(value, context) {
    return typeof value === 'function' ? value.call(context) : value;
}

function cellWidthFixed(width) {
    if (width === undefined || width === null)
        return '';
    if (width === 'auto')
        return '';
    if (width === 'shrink')
        return '.shrink';

    const parsed = parseInt(width);
    if (Number.isNaN(parsed))
        return '';
    if (parsed <= 0)
        return '';
    return `.s${parsed}`;
}

function cursorStyle(name) {
    switch (name) {
        case 'move':
            return '.c-move';
        case 'zoom-in':
            return '.c-zoom-in';
        case 'zoom-out':
            return '.c-zoom-out';
        case 'not-allowed':
            return '.c-not-allowed';
        case 'hand':
            return '.c-hand';
        default:
            return '';
    }
}

export class DataTable extends Component {

    constructor(parent, options = {}) {

        options = Object.assign({
            columns: [],
            data: [],
            dataModel: null,
            queryManager: null,
            editorRouteName: null,
            userCanReadData: true,
            userCanUpdateData: true,
            userCanDeleteData: true,
            onApplyFilter: null,
            onSetAllItemsSelection: null,
            onSetAllItemsDetails: null,
            onToggleItemSelection: null,
            onToggleItemDetails: null,
            activeItem: null,
            isItemActive: null,
            onOpenItem: null,
            onEditItem: null,
            onDeleteItem: null,
        }, options || {});

        super(parent, options);

        this.columns = options.columns;
        this.data = options.data;
        this.dataModel = options.dataModel;
        this.queryManager = options.queryManager;
        this.editorRouteName = options.editorRouteName;
        this.userCanReadData = options.userCanReadData;
        this.userCanUpdateData = options.userCanUpdateData;
        this.userCanDeleteData = options.userCanDeleteData;
        this.onApplyFilter = options.onApplyFilter;
        this.onSetAllItemsSelection = options.onSetAllItemsSelection;
        this.onSetAllItemsDetails = options.onSetAllItemsDetails;
        this.onToggleItemSelection = options.onToggleItemSelection;
        this.onToggleItemDetails = options.onToggleItemDetails;
        this.activeItem = options.activeItem;
        this.isItemActiveCallback = options.isItemActive;
        this.onOpenItem = options.onOpenItem;
        this.onEditItem = options.onEditItem;
        this.onDeleteItem = options.onDeleteItem;
    }

    // ------------------------------------------------------------------------

    getColumns() {
        return resolveValue(this.columns, this);
    }

    getQueryManager() {
        return resolveValue(this.queryManager, this);
    }

    applyFilter() {
        if (!this.onApplyFilter)
            return;
        this.onApplyFilter.call(this);
    }

    // ------------------------------------------------------------------------

    getData() {
        return resolveValue(this.data, this);
    }

    getDataModel() {
        return resolveValue(this.dataModel, this);
    }

    getActiveItem() {
        return resolveValue(this.activeItem, this);
    }

    isItemActive(item) {

        if (typeof this.isItemActiveCallback == 'function')
            return this.isItemActiveCallback.call(this, item) === true;

        return !!item && this.getActiveItem() === item;

    }

    // ------------------------------------------------------------------------

    getEditorRouteName() {
        return resolveValue(this.editorRouteName, this);
    }

    // ------------------------------------------------------------------------

    canUserReadData() {
        return resolveValue(this.userCanReadData, this);
    }

    canUserUpdateData() {
        return resolveValue(this.userCanUpdateData, this);
    }

    canUserDeleteData() {
        return resolveValue(this.userCanDeleteData, this);
    }

    // ------------------------------------------------------------------------

    toggleItemSelection(item) {

        if (!this.onToggleItemSelection)
            return;

        this.onToggleItemSelection.call(this, item);

    }

    toggleItemDetails(item) {

        if (!this.onToggleItemDetails)
            return;

        this.onToggleItemDetails.call(this, item);

    }

    setAllItemsSelection(value) {

        if (!this.onSetAllItemsSelection)
            return;

        this.onSetAllItemsSelection.call(this, value);

    }

    setAllItemsDetails(value) {

        if (!this.onSetAllItemsDetails)
            return;

        this.onSetAllItemsDetails.call(this, value);

    }

    // ------------------------------------------------------------------------

    openItem(item) {

        if (!this.onOpenItem)
            return;

        this.onOpenItem.call(this, item);

    }

    editItem(item) {

        if (!this.onEditItem)
            return;

        this.onEditItem.call(this, item);

    }

    deleteItem(item) {

        if (!this.onDeleteItem)
            return;

        this.onDeleteItem.call(this, item);

    }

    // ------------------------------------------------------------------------

    _render(r, vm) {

        return r.table({ css: '.hover' }, [
            r.thead(this.renderHeader(r, vm)),
            r.tbody(this.renderContent(r, vm)),
        ]);

    }

    renderHeader(r, vm) {

        const columns = this.getColumns() || [];
        const queryManager = this.getQueryManager();
        const queryOrder = (queryManager && queryManager.getQueryOrder()) || [];

        return [
            r.trow([
                this.renderSelectionHeaderCell(r),
                ...columns.map((column) => this.renderHeaderCell(r, column, queryOrder)),
                this.renderDetailHeaderCell(r),
            ])
        ];

    }

    renderContent(r, vm) {

        const i18n = this.getI18n();
        const columns = this.getColumns() || [];
        const data = this.getData() || [];

        if (!data.length) {
            return [
                r.trow([
                    r.tcell({ props: { colspan: columns.length + 1 } }, [
                        r.empty({ title: i18n.t('err.no_records') }),
                    ]),
                ])
            ];
        }

        return data.map((item, rowIndex) => this.renderRow(r, vm, item, rowIndex));

    }

    renderRow(r, vm, item, rowIndex) {

        const columns = this.getColumns() || [];

        if (item.$details) {
            return r.trow(this.getRowRenderStyle(item), [
                this.renderSelectionCell(r, item),
                r.tcell(
                    {
                        css: this.getCellRenderStyle(item),
                        props: Object.assign(this.getCellRenderProps(item) || {}, { colspan: columns.length }),
                    },
                    this.renderItemDetails(r, item)
                ),
                this.renderDetailCell(r, item),
            ]);
        }

        return r.trow(this.getRowRenderStyle(item), [
            this.renderSelectionCell(r, item),
            ...columns.map((column) => this.renderCell(r, item, column)),
            this.renderDetailCell(r, item),
        ]);

    }

    renderItemDetails(r, item) {

        const i18n = this.getI18n();
        const dataModel = this.getDataModel();
        const displayableFields = dataModel?.getDisplayableFields?.() || [];
        const buttons = [];

        if (this.canUserReadData()) {
            buttons.push(r.iconButton('#open', { css: '.circle.tonal', props: { onclick: () => this.openItem(item), title: i18n.t('button.open') } }));
        }
        if (this.canUserUpdateData()) {
            buttons.push(r.iconButton('#edit', { css: '.circle.tonal', props: { onclick: () => this.editItem(item), title: i18n.t('button.edit') } }));
        }
        if (this.canUserDeleteData()) {
            buttons.push(r.iconButton('#delete', { css: '.circle.tonal', props: { onclick: () => this.deleteItem(item), title: i18n.t('button.delete') } }));
        }

        return [
            r.el('article', null, null, [
                r.header({}, [
                    r.nav({}, [
                        r.div('.max'),
                        ...buttons
                    ]),
                ]),
                r.section({}, [
                    r.grid({},
                        displayableFields.map((field) => {
                            const value = field.getFormattedValue ? field.getFormattedValue(item) : field.getValue(item);
                            return r.cell({ css: cellWidthFixed(field.displayWidth || 6) }, [
                                r.field({}, [
                                    r.label([field.label || field.displayName || field.name || '']),
                                    r.div('.control', [
                                        (value === null || value === undefined || value === '') ? '\u2014' : String(value),
                                    ]),
                                ]),
                            ]);
                        })
                    ),
                ]),
            ])
        ];

    }

    // ----------------------------------------------------

    getHeaderCellRenderStyle(column) {

        if (!column)
            return '.no-wrap';

        let style = '';

        if (column.wordWrap === false)
            style += '.text-ellipsis';

        if (column.width)
            style += cellWidthFixed(column.width);

        return style;

    }

    getHeaderCellRenderProps(column) {
        return {};

    }

    // ----------------------------------------------------

    getRowRenderStyle(item) {

        if (!item)
            return '';

        let style = '';

        if (item.$selected)
            style += '.selected.bg-gray';
        if (this.isItemActive(item))
            style += '.active';

        return style;

    }

    // ----------------------------------------------------

    getCellRenderStyle(item, column) {

        if (!item)
            return '.no-wrap';

        if (!column)
            return '';

        let style = '';

        switch (column.alignment) {
            case 'left':
                style += '.left-align';
                break;
            case 'right':
                style += '.rigth-align';
                break;
            case 'center':
                style += '.center-align';
                break;
            case 'justify':
                style += '.justify-align';
                break;
            default:
                style += (dtt.isNumericType(column.field?.type) ? '.right-align' : '.left-align');
                break;
        }

        switch (column.cursor) {
            case 'move':
                style += '.c-move';
                break;
            case 'zoom-in':
                style += '.c-zoom-in';
                break;
            case 'zoom-out':
                style += '.c-zoom-out';
                break;
            case 'not-allowed':
                style += '.c-not-allowed';
                break;
            default:
                style += '.c-hand';
                break;
        }

        if (column.wordWrap === false)
            style += '.no-wrap';

        if (column.width)
            style += cellWidthFixed(column.width);

        return style;

    }

    getCellRenderProps(item, column) {

        if (!item) {
            return {
                style: 'vertical-align:top;'
            };
        }

        if (!column)
            return {};

        return {
            onclick: () => {
                if (column.onClick) {
                    column.onClick(item);
                    return;
                }
                this.openItem(item);
            }
        };

    }

    // ---------------------------------------------------- Content

    renderSelectionHeaderCell(r) {

        const i18n = this.getI18n();

        if (!this.onToggleItemSelection) {
            return r.thcell({ css: this.getHeaderCellRenderStyle(), props: this.getHeaderCellRenderProps() }, []);
        }

        const menuItems = [
            r.menuItem(null, null, {
                props: {
                    onclick: () => {
                        this.setAllItemsSelection(true);
                    }
                },
            }, null, [
                r.link(null, [
                    r.icon('square-check'),
                    r.span([i18n.t('button.items_select_all')]),
                ])
            ]),
            r.menuItem(null, null, {
                props: {
                    onclick: () => {
                        this.setAllItemsSelection(false);
                    }
                },
            }, null, [
                r.link(null, [
                    r.icon('square'),
                    r.span([i18n.t('button.items_unselect_all')]),
                ]),
            ]),
        ];

        return r.thcell(
            { css: this.getHeaderCellRenderStyle(), props: this.getHeaderCellRenderProps() },
            [
                r.dropdownButton(null, '#expand', { buttonCss: '.circle.transparent', anchor: 'bottom right' }, menuItems),
            ]
        );

    }

    renderDetailHeaderCell(r) {

        const i18n = this.getI18n();

        if (!this.onToggleItemDetails) {
            return r.thcell({ css: this.getHeaderCellRenderStyle(), props: this.getHeaderCellRenderProps() }, []);
        }

        const menuItems = [
            r.menuItem(null, null, { props: { onclick: () => { this.setAllItemsDetails(true); } } }, null, [
                r.link(null, [
                    r.icon('chevron-down'),
                    i18n.t('button.items_expand_all'),
                ]),
            ]),
            r.menuItem(null, null, { props: { onclick: () => { this.setAllItemsDetails(false); } } }, null, [
                r.link(null, [
                    r.icon('chevron-up'),
                    i18n.t('button.items_collapse_all'),
                ]),
            ]),
        ];

        return r.thcell(
            { css: this.getHeaderCellRenderStyle(), props: this.getHeaderCellRenderProps() },
            [
                r.dropdownButton(null, '#expand', { buttonCss: '.circle.transparent', anchor: 'bottom left' }, menuItems)
            ]
        );

    }

    renderHeaderCell(r, column, queryOrder) {

        const i18n = this.getI18n();

        if (!column)
            return null;

        let content;

        if (!column.orderable) {
            content = [column.label];
        } else {
            let orderNode = queryOrder.find(function (item) {
                return item.ident == column.fieldName;
            });
            let icon = orderNode ? (orderNode.direction === 'asc' ? '#sort-up' : '#sort-down') : '#sort';
            let buttonCss = '.circle.transparent' + (orderNode ? '.active' : '');

            content = [
                column.label,
                '\u00A0',
                r.iconButton(icon, {
                    css: buttonCss,
                    props: {
                        onclick: (e) => this.getQueryManager().updateOrder(column.fieldName, e.ctrlKey),
                        title: i18n.t('module.browser.hint_orderable_column')
                    },
                }),
            ];
        }

        return r.thcell(
            { css: this.getHeaderCellRenderStyle(column), props: this.getHeaderCellRenderProps(column) },
            content
        );

    }

    renderCell(r, item, column) {

        const app = this.getParent().getApp();

        let content = [];
        let field = column.field;

        if (field) {
            const value = field.getValue(item);

            if (value !== null && value !== undefined) {
                if (dtt.isBoolType(field.type)) {
                    content = [
                        r.icon(value ? 'check' : 'close')
                    ];
                } else if (field.type == 'File') {
                    const url = app.getFileThumbnailUrl(value);
                    let css = '.p-relative' + cursorStyle('zoom-in');
                    let nodes = [];

                    if (column.onClick) {
                        nodes.push(
                            r.iconButton('#search', { css: '.small.absolute', props: { onclick: () => column.onClick(item), style: 'right:0.25rem;bottom:0.25rem;' } })
                        );
                    }

                    nodes.push(r.a({ css, props: { href: url } }, [
                        r.img({ css: 'img-responsive', props: { src: url } }, [])
                    ]));

                    content = nodes;
                } else if (field.formatter) {
                    //content = [field.formatter.format(value, column.format || field.format)];
                    content = [field.getFormattedValue(item, column.format)];
                }
            }
        }

        return r.tcell(
            { css: this.getCellRenderStyle(item, column), props: this.getCellRenderProps(item, column) },
            content
        );

    }

    renderSelectionCell(r, item, content = null) {

        if (!content) {
            content = [];

            if (this.onToggleItemSelection) {
                content.push(
                    r.iconButton(item.$selected ? '#checked' : '#unchecked', { css: '.circle.transparent', props: { onclick: () => this.onToggleItemSelection(item) } })
                );
            }
        }

        return r.tcell(
            { css: this.getCellRenderStyle(), props: this.getCellRenderProps() },
            content
        );

    }

    renderDetailCell(r, item, content = null) {

        if (!content) {
            content = [];

            if (this.onToggleItemDetails) {
                content.push(
                    r.iconButton(item.$details ? '#collapse' : '#expand', { css: '.circle.transparent', props: { onclick: () => this.onToggleItemDetails(item) } })
                );
            }
        }

        return r.tcell(
            { css: this.getCellRenderStyle(), props: this.getCellRenderProps() },
            content
        );

    }

}
